/**
 * How much of the screen a row is allowed to take (T-059).
 *
 * ── THE PROBLEM, MEASURED ───────────────────────────────────────────────────
 *
 * The shop PC is 1366×768. After the browser's own chrome and this app's fixed
 * top bar, roughly 600px of page is left. A DataTable row is 44px with its
 * borders, so a 50-row page shows about eleven rows at a time — the footer, the
 * pager and the row count are all four screens of scrolling away.
 *
 * Staff scan these lists. Somebody looking for one invoice among sixty is
 * paging through six screenfuls to find it, all day, and the fix is not a
 * faster search box: it is putting more of the list in front of them.
 *
 * ── WHAT COMPACT CHANGES, AND WHAT IT DOES NOT ──────────────────────────────
 *
 * Padding, row height and control height. **Not type size.** That distinction
 * matters more here than it would in most applications: these screens are read
 * across a counter, often at an angle, sometimes by someone reading a customer
 * their own total upside down. Shrinking the text to fit more rows would trade a
 * real problem for a worse one.
 *
 * At compact a row is 32px instead of 44, so the same 600px shows sixteen rows
 * rather than eleven — about a 45% increase, from padding alone.
 *
 * ── WHY THIS IS STORED DIFFERENTLY FROM THE THEME ───────────────────────────
 *
 * Theme is a property of the SCREEN (theme.js explains why: the counter PC by
 * the window wants light whoever is sitting at it). Density is a property of the
 * PERSON — it is about eyesight and about how somebody reads a list — so it goes
 * through the shop-and-user-scoped store, and two people sharing one shop PC
 * each keep their own.
 */

import { getItem, setItem } from '../storage/scopedStorage'

export const DENSITIES = ['comfortable', 'compact']

/**
 * Comfortable, not compact.
 *
 * A denser interface is better for the person who has chosen it and worse for
 * everyone who has not — smaller targets, less separation between rows, more to
 * take in at once. The default should be the one that is safe for somebody who
 * has never seen this control, and the shop can opt in.
 */
export const DEFAULT_DENSITY = 'comfortable'

const STORAGE_KEY = 'density'

export function getStoredDensity() {
    const stored = getItem(STORAGE_KEY)
    return DENSITIES.includes(stored) ? stored : DEFAULT_DENSITY
}

/**
 * Put the choice on <html>, where ui.css is watching for it.
 *
 * An attribute rather than a class, matching data-theme, so the two live in the
 * same place and a stylesheet reads `[data-density='compact']` the same way it
 * reads `[data-theme='dark']`.
 */
export function applyDensity(density = getStoredDensity()) {
    if (typeof document === 'undefined') return
    const next = DENSITIES.includes(density) ? density : DEFAULT_DENSITY
    document.documentElement.setAttribute('data-density', next)
}

export function setDensity(density) {
    const next = DENSITIES.includes(density) ? density : DEFAULT_DENSITY
    setItem(STORAGE_KEY, next)
    applyDensity(next)
    return next
}

/**
 * Called once before React renders, alongside initTheme.
 *
 * ⚠️ Unlike the theme, this one CANNOT run before login and be right. The
 * scoped store keys by shop and user, and before a session exists it resolves
 * to the anonymous bucket — so the density applied at boot is the anonymous
 * one, and the user's own is applied when their session is known. That is
 * correct behaviour and not a bug to fix: the alternative is leaking one
 * person's preference to whoever logs in next on the same machine, which is
 * precisely what the scoping exists to prevent (T-020).
 */
export function initDensity() {
    applyDensity(getStoredDensity())
}
